import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";

import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import getQueryKeys from "common/datahooks/getQueryKeys";
import { del, patch, post } from "common/helpers/HTTP";
import { getCountryName } from "common/helpers/utils";

import type { CustomProxy } from "../types";

type CustomProxyUpdates = Omit<CustomProxy, "id" | "location" | "valid">;

async function deleteProxyRequest(workspaceId: string, proxyId: string) {
  await del(`workspaces/${workspaceId}/custom_proxies/${proxyId}`);
}

async function updateProxyRequest(
  workspaceId: string,
  webhookId: string,
  updates: CustomProxyUpdates,
): Promise<CustomProxy> {
  const { proxy } = await patch(
    `workspaces/${workspaceId}/custom_proxies/${webhookId}`,
    updates,
  );
  return {
    ...proxy,
    location: {
      ...proxy.location,
      country: getCountryName(proxy.location.countryISO),
    },
  };
}

async function createProxyRequest(
  workspaceId: string,
  newProxy: CustomProxyUpdates,
): Promise<CustomProxy> {
  const { proxy } = await post(
    `workspaces/${workspaceId}/custom_proxies`,
    newProxy,
  );
  return {
    ...proxy,
    location: {
      ...proxy.location,
      country: getCountryName(proxy.location.countryISO),
    },
  };
}

export default function useWorkspaceProxyMutations() {
  const queryClient = useQueryClient();
  const { id: workspaceId } = useSelectedWorkspaceContext();
  const { proxiesKeys } = getQueryKeys(workspaceId);

  const { mutateAsync: deleteProxy, isPending: isDeletingProxy } = useMutation({
    mutationFn: ({ proxyId }: { proxyId: string }) =>
      deleteProxyRequest(workspaceId, proxyId),
    onSuccess: (_, { proxyId }) => {
      queryClient.setQueryData<CustomProxy[]>(proxiesKeys.list, (prevProxies) =>
        prevProxies.filter(({ id }) => id !== proxyId),
      );

      toast.success("Proxy deleted");
    },
    onError: () => toast.error("Error deleting Proxy"),
  });

  const {
    mutateAsync: updateProxy,
    isPending: isUpdatingProxy,
    error: updateProxyError,
  } = useMutation({
    mutationFn: ({
      proxyId,
      updates,
    }: {
      proxyId: string;
      updates: CustomProxyUpdates;
    }) => updateProxyRequest(workspaceId, proxyId, updates),
    onSuccess: (newProxy, { proxyId }) => {
      queryClient.setQueryData<CustomProxy[]>(proxiesKeys.list, (prevProxies) =>
        prevProxies.map((proxy) => {
          if (proxy.id === proxyId) {
            return { ...newProxy, valid: true };
          }
          return { ...proxy, valid: true };
        }),
      );
      toast.success("Proxy updated");
    },
  });

  const {
    mutateAsync: createProxy,
    isPending: isCreatingProxy,
    error: createProxyError,
  } = useMutation({
    mutationFn: (newProxy: CustomProxyUpdates) =>
      createProxyRequest(workspaceId, newProxy),
    onSuccess: (newProxy) => {
      queryClient.setQueryData<CustomProxy[]>(
        proxiesKeys.list,
        (prevProxies) => [{ ...newProxy, valid: true }, ...prevProxies],
      );

      toast.success("Proxy created");
    },
  });

  return {
    deleteProxy,
    isDeletingProxy,
    updateProxy,
    isUpdatingProxy,
    updateProxyError,
    createProxy,
    isCreatingProxy,
    createProxyError,
  };
}
